import { useEffect, useState, useCallback } from 'react';
import { useMount, useSessionStorage, useUnmount } from 'react-use';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import moment from 'moment';
import PageHeader from '../../uiKitComponents/pageHeader';
import Table from '../../uiKitComponents/table';
import Form from '../../uiKitComponents/form';
import DatePicker from '../../uiKitComponents/datePicker';
import Alert from '../../uiKitComponents/alert';
import Button from '../../uiKitComponents/button';
import * as reports from '../../store/actions/reportPagesAction';
import buildTableColumns from '../../utils/buildTableColumns';

const { RangePicker } = DatePicker;

const dateFormat = 'YYYY-MM-DD';

const initialState = {
  page: 1,
  sort_column: 'id',
  sort_direction: 'descend',
  dates: { from: { string: '', locale: '' }, to: { string: '', locale: '' } },
  roleType: 1,
  reportV: 1,
  filtersTrigger: false,
};

const SurveysReportPage = () => {
  const [state, setState] = useSessionStorage('surveys-reports', {
    ...initialState,
  });
  const { page, filtersTrigger, dates } = state;
  const [error, setError] = useState();
  const [loading, setLoading] = useState(false);
  const { cleanOnUnmount, surveysReport } = useSelector((store) => ({
    cleanOnUnmount: store.reportPages.cleanOnUnmount,
    surveysReport: store.reportPages.payload.surveysReport,
  }));
  const dispatch = useDispatch();

  useMount(() => {
    reports.setCleanOnUnmountTrue()(dispatch);
  });

  const fetchSurveysReports = () => {
    setLoading(true);

    reports
      .getSurveysReports({
        from: dates.from.string,
        to: dates.to.string,
        is_new_version: state.reportV,
      })(dispatch)
      .catch((err) => {
        if (err.response.status === 500) {
          setError({
            status: err.response.status,
            message: err.response.data.message,
          });
          sessionStorage.removeItem('surveys-reports');
        }
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchSurveysReports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersTrigger, dispatch, dates]);

  useUnmount(() => {
    if (cleanOnUnmount) {
      sessionStorage.removeItem('surveys-reports');
      reports.cleanReports()(dispatch);
    }
  });

  const onTableChange = useCallback(
    (pagination, tableFilters, sorter, extra) => {
      const newState = {};
      if (extra.action === 'paginate') newState.page = pagination.current;
      else if (extra.action === 'sort') {
        newState.sort_column = sorter.field;
        newState.sort_direction = sorter.order;
      }
      setState((prevState) => ({ ...prevState, ...newState }));
    },
    [setState],
  );

  const onSelectedDatesHandler = (values) => {
    const { range } = values;

    if (range && range.length) {
      const date_from = dayjs(range[0].$d).format('DD-MM-YYYY');
      const date_to = dayjs(range[1].$d).format('DD-MM-YYYY');

      setState((prevState) => ({
        ...prevState,
        dates: {
          from: { string: date_from, locale: range[0] },
          to: { string: date_to, locale: range[1] },
        },
      }));
    } else {
      setState((prevState) => ({
        ...prevState,
        dates: {
          from: { string: '', locale: '' },
          to: { string: '', locale: '' },
        },
      }));
    }
  };

  const getMomentDate = (date) => {
    if (date) return moment(date, dateFormat);

    return null;
  };

  if (error)
    return (
      <PageHeader className="page-main-header" title="Engagement - Fav/Rev">
        <Alert
          showIcon
          type="error"
          message={`Error ${error.status}`}
          description={error.message}
        />
      </PageHeader>
    );

  return (
    <div>
      <PageHeader className="page-main-header" title="360 Surveys" />
      <Form layout="inline" className="mb-3" onFinish={onSelectedDatesHandler}>
        <Form.Item
          name="range"
          label="Select Dates"
          initialValue={[
            getMomentDate(dates.from?.locale),
            getMomentDate(dates.to?.locale),
          ]}
        >
          <RangePicker
            format="DD/MM/YYYY"
            disabledDate={(date) => date.isAfter(moment())}
          />
        </Form.Item>
        <Button type="primary" htmlType="submit">
          Select
        </Button>
      </Form>
      <div className="page-main-content">
        <Table
          columns={buildTableColumns(surveysReport?.columns)}
          rowKey={(e) => e.id}
          dataSource={surveysReport?.data || []}
          loading={loading}
          onChange={onTableChange}
          pagination={{
            position: ['topRight', 'bottomRight'],
            showSizeChanger: false,
            showQuickJumper: true,
            pageSize: 25,
            total: surveysReport?.meta?.total,
            current: page,
            hideOnSinglePage: true,
          }}
        />
      </div>
    </div>
  );
};

SurveysReportPage.defaultProps = {
  reportPageProps: {},
  row_action: [],
  row_inner_action: [],
};

SurveysReportPage.propTypes = {
  reportPageProps: PropTypes.shape({
    sessionStorageName: PropTypes.string,
    pageTitle: PropTypes.string,
    stateName: PropTypes.string,
    actionName: PropTypes.string,
    default_sort_column: PropTypes.string,
  }),
  row_action: PropTypes.array,
  row_inner_action: PropTypes.array,
};

export default SurveysReportPage;
